import React from 'react';
import { Skeleton } from '@vectord/components';
import classNames from 'classnames';

import { useGetSbatLecturesQuery } from '@courseResume/services/coursePauseRequestService';
import addDaysToDate from 'utils/addDaysToDate';
import CoursePauseSbatLectureRow from '../CoursePauseSbatLectureRow';
import ErrorLayout from 'components/ErrorLayout';
import Field from 'components/Field';
import getNextSbatLectureId from '@courseResume/utils/getNextSbatLectureId';
import GlobalLoader from 'components/GlobalLoader';
import styles from './CoursePauseBatchLecturesList.module.scss';

function CoursePauseBatchLecturesList({
  superBatch,
  coursePauseRequest,
  referenceDate,
  selectedLecture,
  ...remainingProps
}) {
  // We need previous 6 months (-180 days) classes and
  // 6 months (+180 days) future classes.
  const fromDate = addDaysToDate({
    daysToAdd: -180,
    currentDate: coursePauseRequest?.pause_date,
  }).toDateString();
  const toDate = addDaysToDate({
    daysToAdd: 180,
    currentDate: new Date().toDateString(),
  }).toDateString();
  const { isLoading, isFetching, error, data, refetch } =
    useGetSbatLecturesQuery({ superBatch, fromDate, toDate });

  if (isLoading) {
    return (
      <>
        <Skeleton
          width="80%"
          height="2rem"
          className="m-v-xs"
        />
        <div className={styles.skeletonContainer}>
          {new Array(5).fill(0).map((_, index) => (
            <Skeleton
              key={index}
              width="90%"
              height="2rem"
              className="m-v-xxs"
            />
          ))}
        </div>
      </>
    );
  } else if (error) {
    return (
      <ErrorLayout
        title="Failed to load lectures!"
        message={error.data?.message || 'Something went wrong! Try again later'}
        actionFn={refetch}
        className={styles.errorLayout}
        titleClassName="h5"
        messageClassName="h6"
        actionLabelClassName="btn-small"
      />
    );
  } else if (data?.data?.length > 0) {
    const lecturesList = data.data;
    const nextSbatId = getNextSbatLectureId(lecturesList, referenceDate);

    return (
      <>
        <GlobalLoader isVisible={isFetching} />
        <Field
          label={superBatch.name}
          className="h5"
        >
          <div className={classNames(styles.listContainer, 'scroll')}>
            {lecturesList.map((lecture) => (
              <CoursePauseSbatLectureRow
                lecture={lecture}
                key={lecture.id}
                isSelected={
                  selectedLecture
                    ? selectedLecture?.id === lecture.id
                    : lecture.id === nextSbatId
                }
                {...remainingProps}
              />
            ))}
          </div>
        </Field>
      </>
    );
  } else {
    return (
      <ErrorLayout
        title="Lectures Not Found!"
        message="No lecture found for selected super batch."
        actionFn={refetch}
        className={styles.errorLayout}
        titleClassName="h5"
        messageClassName="h6"
        actionLabelClassName="btn-small"
      />
    );
  }
}

export default CoursePauseBatchLecturesList;
